/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import React, { useRef, useEffect } from 'react';
import { Gift, ChevronUp, ChevronDown, MoreHorizontal, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubscriptionCard from './SubscriptionCard';
import { SubscriptionState } from '../../types';
import PageHeader from '../ui/PageHeader';

interface SubscriptionHeaderProps {
  subscriptions: SubscriptionState[];
  activeSubscriptions: SubscriptionState[];
  cancelledSubscriptions: SubscriptionState[];
  activeSubscriptionIndex: number;
  showCancelledSubscriptions: boolean;
  setShowCancelledSubscriptions: (show: boolean) => void;
  setActiveSubscriptionIndex: (index: number) => void;
  hideTitle?: boolean;
}

const SubscriptionHeader: React.FC<SubscriptionHeaderProps> = ({
  subscriptions,
  activeSubscriptions,
  cancelledSubscriptions,
  activeSubscriptionIndex,
  showCancelledSubscriptions,
  setShowCancelledSubscriptions,
  setActiveSubscriptionIndex,
  hideTitle = false
}) => {
  const navigate = useNavigate();
  const cardContainerRef = useRef<HTMLDivElement | null>(null);

  // Sort active subscriptions by startDate, newest first
  const sortedActiveSubscriptions = [...activeSubscriptions].sort((a, b) => {
    const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
    const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
    return dateB - dateA; // Newest first
  });

  // Sort cancelled subscriptions by startDate, newest first
  const sortedCancelledSubscriptions = [...cancelledSubscriptions].sort((a, b) => {
    const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
    const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
    return dateB - dateA; // Newest first
  });

  const handleSelect = (index: number) => {
    // Don't do anything if clicking the already active subscription
    if (index === activeSubscriptionIndex) return;
    
    
    console.log('Selected subscription:', {
      currentIndex: activeSubscriptionIndex,
      newIndex: index,
      subscription: subscriptions[index]
    });
    
    
    setActiveSubscriptionIndex(index);
    
    // Scroll to the selected card immediately without delay
    setTimeout(() => {
      const allCards = cardContainerRef.current?.children;
      if (allCards) {
        // Find the actual DOM element that corresponds to the subscription
        const selectedId = subscriptions[index]?.id;
        let cardElement = null;
        
        // Find the card element with matching subscription index data attribute
        for (let i = 0; i < allCards.length; i++) {
          const card = allCards[i] as HTMLElement;
          if (card.dataset && card.dataset.subscriptionIndex === index.toString()) {
            cardElement = card;
            break;
          }
        }
        
        // If found, scroll to it
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    }, 10);
  };

  
   const logSelectionState = () => {
    console.log('Selection state:', {
      activeSubscriptionIndex,
      totalSubscriptions: subscriptions.length,
      activeSubscriptions: activeSubscriptions.length,
      cancelledSubscriptions: cancelledSubscriptions.length
    });
    
    
    subscriptions.forEach((sub, idx) => {
      console.log(`Subscription ${idx}:`, {
        id: sub.id,
        status: sub.status,
        isActive: idx === activeSubscriptionIndex,
        isGift: sub.isGift,
        giftType: sub.giftType
      });
    });
    
    // Log gift subscriptions
    const giftSubs = subscriptions.filter(sub => sub.isGift);
    console.log('Gift subscriptions:', giftSubs);
  };
  
  
  useEffect(() => {
    logSelectionState();
  }, [activeSubscriptionIndex, subscriptions.length]);

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden"> 
      <div className="">
        <div className="flex justify-between items-center">
          {!hideTitle && (
            <PageHeader title="Your Subscriptions" backPath="/me" />
          )}
        </div>
        
        {/* Show selector if multiple active subscriptions OR any inactive subscriptions */}
        {false && (activeSubscriptions.length > 1 || cancelledSubscriptions.length > 0) && (
          <div className="p-4 bg-white rounded-lg">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" ref={cardContainerRef} style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Active/Paused Subscriptions */}
              {sortedActiveSubscriptions.map((sub, idx) => {
                const actualIndex = subscriptions.findIndex(s => s.id === sub.id);
                console.log(`Rendering active card ${idx}:`, { 
                  id: sub.id, 
                  actualIndex, 
                  isActive: actualIndex === activeSubscriptionIndex,
                  isGift: sub.isGift,
                  giftType: sub.giftType
                });
                return (
                  <SubscriptionCard
                    key={sub.id || idx}
                    subscription={sub}
                    isActive={actualIndex === activeSubscriptionIndex}
                    onSelect={() => handleSelect(actualIndex)}
                    subscriptionIndex={actualIndex}
                  />
                );
              })}
              
              {/* Only show a divider if there are active subscriptions and we're showing inactive ones */}
              {activeSubscriptions.length > 0 && cancelledSubscriptions.length > 0 && showCancelledSubscriptions && (
                <div className="border-l h-10 self-center mx-1 border-gray-200"></div>
              )}
              
              {/* Show/Hide Inactive Subscriptions Toggle - with different styles based on active subscription count */}
              {cancelledSubscriptions.length > 0 && (
                <button
                  onClick={() => setShowCancelledSubscriptions(!showCancelledSubscriptions)}
                  className={`my-1 ${
                    activeSubscriptions.length === 1 
                      ? "py-1 px-2 text-gray-400 border-0 hover:bg-gray-50" 
                      : "py-2 px-3 text-gray-700 border border-gray-200 hover:bg-gray-50"
                  } rounded-lg text-xs flex justify-between items-center whitespace-nowrap ${
                    activeSubscriptions.length === 1 ? "min-w-[40px]" : "min-w-[80px]"
                  }`}
                >
                  {activeSubscriptions.length === 1 ? (
                    <>
                      {showCancelledSubscriptions ? (
                        <span className="flex items-center justify-center w-full">
                          <ChevronUp className="w-3 h-3 text-gray-400" />
                          <span className="ml-1 text-gray-400">({cancelledSubscriptions.length})</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center w-full">
                          <MoreHorizontal className="w-3 h-3 text-gray-400" />
                          <span className="ml-1 text-gray-400">({cancelledSubscriptions.length})</span>
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      <span>
                        {showCancelledSubscriptions ? '<' : '...'} ({cancelledSubscriptions.length})
                      </span>
                      {showCancelledSubscriptions ? (
                        <ChevronUp className="w-3 h-3 ml-1 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-3 h-3 ml-1 text-gray-400" />
                      )}
                    </>
                  )}
                </button>
              )}
              
              {/* Inactive Subscriptions (if expanded) */}
              {showCancelledSubscriptions && sortedCancelledSubscriptions.map((sub, idx) => {
                const actualIndex = subscriptions.findIndex(s => s.id === sub.id);
                console.log(`Rendering inactive card ${idx}:`, { 
                  id: sub.id, 
                  actualIndex, 
                  isActive: actualIndex === activeSubscriptionIndex,
                  isGift: sub.isGift,
                  giftType: sub.giftType
                });
                return (
                  <SubscriptionCard
                    key={sub.id || `cancelled-${idx}`}
                    subscription={sub}
                    isActive={actualIndex === activeSubscriptionIndex}
                    onSelect={() => handleSelect(actualIndex)}
                    subscriptionIndex={actualIndex}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionHeader; 